import type { MrzParseResult } from "../utils/mrz/mrz";
import { useIdScanSession, type IdScanState, type UseIdScanSessionReturn } from "./useIdScanSession";
import { useMrzScanner } from "./useMrzScanner";
import { isMrzOcrReady, mrzOcrUnavailableReason } from "../services/ocr/mrzOcrWorker";

// EN-4743. One passport capture: the MRZ on the photo page is read live during the
// countdown (see useIdScanSession for the flow). No second read of the photo
// after the last miss: the live OCR already tried several crops of the same view.
export { MAX_SCAN_ATTEMPTS } from "./useIdScanSession";
export type PassportScanState = IdScanState;

const PASSPORT_EVENTS = {
  read: "id_mrz_read",
  attemptFailed: "id_mrz_attempt_failed",
  fallbackSubmit: "id_mrz_fallback_submit",
  unavailable: "id_mrz_ocr_unavailable",
  rejected: "id_mrz_rejected",
};

interface UsePassportScanSessionOptions {
  enabled: boolean;
  videoEl: HTMLVideoElement | null | undefined;
  stageEl: Element | null;
  guideEl: Element | null;
  // Takes the photo, the same way the Capture button does.
  takePhoto: () => void;
  // Sends the photo for validation without the visitor tapping Continue.
  autoSubmit: () => void;
}

type UsePassportScanSessionReturn = Omit<UseIdScanSessionReturn<MrzParseResult>, "data"> & {
  mrzData: MrzParseResult | null;
};

export const usePassportScanSession = (options: UsePassportScanSessionOptions): UsePassportScanSessionReturn => {
  const { data, ...session } = useIdScanSession<MrzParseResult>({
    ...options,
    useScanner: useMrzScanner,
    isReaderReady: isMrzOcrReady,
    readerUnavailableReason: mrzOcrUnavailableReason,
    events: PASSPORT_EVENTS,
  });
  return { ...session, mrzData: data };
};
